/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (CURRENT) - Initial creation of the whitelabel header handler utility.
 *
 * -----
 * SECURITY_RISK: LOW - This utility provides a mechanism to register and call a global function
 *                      for updating whitelabel configuration from an HTTP header. It does not
 *                      directly handle sensitive data or perform complex logic.
 * INTENT: Centralize the registration and invocation of a callback function to update
 *         the whitelabel configuration based on an `X-White-Label` HTTP header.
 * PROMPTER_INSTRUCTIONS: Ensure the registered handler correctly processes the incoming
 *                        configuration and updates the application state.
 * LLM_CONFIDENCE: 100% - Standard global callback registration pattern.
 */
import { WhitelabelConfig } from '../types/whitelabel';

// Define the type for the handler function
type WhitelabelHeaderHandler = (config: WhitelabelConfig | null) => void;

let whitelabelHeaderHandler: WhitelabelHeaderHandler | null = null;

/**
 * Registers a global handler function that will be called when a whitelabel config
 * is received from an HTTP header.
 * @param handler The function to register.
 */
export const registerWhitelabelHeaderHandler = (handler: WhitelabelHeaderHandler) => {
  whitelabelHeaderHandler = handler;
};

/**
 * Calls the registered global whitelabel header handler with the provided configuration.
 * If no handler is registered, it logs a warning.
 * @param config The WhitelabelConfig object to pass to the handler.
 */
export const setWhitelabelConfigFromHeader = (config: WhitelabelConfig | null) => {
  if (whitelabelHeaderHandler) {
    whitelabelHeaderHandler(config);
  } else {
    console.warn("Whitelabel header handler not registered. Configuration from header will not be applied.");
  }
};
